import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  CalendarSlotsRequest,
  CalendarSlotsResponse,
} from '@/types/scheduling-api';
import { setPracticeTimeZone } from '@/shared/lib/practice-time';

/**
 * Hits POST /api/v1/Practice/calendarSlots — the Flutter calendar grid feed.
 * Returns provider-organized slot data, NOT a flat appointment list. Use
 * `mapCalendarSlotsToAppointments` to flatten for grid components.
 *
 * Mirrors Flutter's CalendarSlotsEvent → SchedulingDataSourceImpl.calendarSlots().
 */

interface CalendarSlotsParams {
  organizationId: string;
  practiceId: string;
  /** ISO date or full datetime; the hook ensures the day-end behavior. */
  startDate: string;
  endDate: string;
  practitioners?: string[];
  searchText?: string;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Flutter's datasource bumps endDate to 23:59:59 before sending. We mirror
 * that contract: callers can pass `YYYY-MM-DD` or a full ISO and we'll
 * normalize to a full-day range.
 */
function toDayBoundaryISO(input: string, end: boolean): string {
  // Already a full datetime — pass through.
  if (input.includes('T')) return input;
  return end ? `${input}T23:59:59Z` : `${input}T00:00:00Z`;
}

export function useCalendarSlots(params: CalendarSlotsParams | null) {
  return useQuery({
    queryKey: ['calendar-slots', params],
    queryFn: async ({ signal }) => {
      if (!params) throw new Error('useCalendarSlots called without params');
      const body: CalendarSlotsRequest = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        startDate: toDayBoundaryISO(params.startDate, false),
        endDate: toDayBoundaryISO(params.endDate, true),
        practitioners: params.practitioners ?? [],
        searchText: params.searchText ?? '',
        pageNumber: params.pageNumber ?? 0,
        pageSize: params.pageSize ?? 5,
      };
      const res = await post<CalendarSlotsResponse>('/api/v1/Practice/calendarSlots', body, { signal });
      // A practice with no slots returns an empty/null body. React Query
      // rejects an `undefined` return ("data cannot be undefined") which
      // would fire the global error toast — coalesce to [] (the response is
      // ProviderWithSlots[], and the mappers already handle empty).
      // The practice's zone, when the response names it (option A, 16 Oct); otherwise the default stays.
      const zone = res?.find((p) => p.organizationTimeZone)?.organizationTimeZone;
      if (zone) setPracticeTimeZone(zone);
      return res ?? [];
    },
    enabled: !!params?.practiceId,
    refetchOnMount: 'always',
  });
}
